import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  SG_GET_ORG_GROUP_MEMBERS, 
  SG_BULK_ADD_ORG_GROUP_MEMBERS, 
  SG_BULK_REMOVE_ORG_GROUP_MEMBERS 
} from 'constants/actions';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  List, 
  Header, 
  Loader, 
  Message, 
  Pagination, 
  Input, 
  Segment, 
  Button, 
  Icon, 
  Label,
  Grid,
  Divider,
  Form,
  TextArea
} from 'semantic-ui-react';
import styled from 'styled-components';

const ListEmployees = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { orgMembers, loading, error, bulkAddResult, bulkRemoveResult } = useSelector(state => state.groups);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [employeeIdsToAdd, setEmployeeIdsToAdd] = useState('');
  const [employeeIdsToRemove, setEmployeeIdsToRemove] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showBulkRemove, setShowBulkRemove] = useState(false);
  const pageSize = 12;

  useEffect(() => {
    if (id) {
      const payload = { id, organization_groups: id, page, page_size: pageSize };
      if (search) payload.search = search;
      dispatch({ type: SG_GET_ORG_GROUP_MEMBERS, payload });
    }
  }, [dispatch, id, page, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on new search
  };

  useEffect(() => {
    if (orgMembers && orgMembers.count && orgMembers.page_size) {
      setTotalPages(Math.ceil(orgMembers.count / orgMembers.page_size));
    }
  }, [orgMembers]);

  const handlePageChange = (event, { activePage }) => {
    setPage(activePage);
  };

  const handleBulkAdd = () => {
    const ids = employeeIdsToAdd
      .split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));
    
    if (ids.length === 0) {
      alert('Please enter valid employee IDs');
      return;
    }

    dispatch({
      type: SG_BULK_ADD_ORG_GROUP_MEMBERS,
      payload: { id, employee_ids: ids }
    });
    setEmployeeIdsToAdd('');
  };

  const handleBulkRemove = () => {
    const ids = employeeIdsToRemove
      .split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));
    
    if (ids.length === 0) {
      alert('Please enter valid employee IDs');
      return;
    }

    dispatch({
      type: SG_BULK_REMOVE_ORG_GROUP_MEMBERS,
      payload: { id, employee_ids: ids }
    });
    setEmployeeIdsToRemove('');
  };

  const renderBulkResult = (result, isAdd) => {
    if (!result || result.error) {
      if (result?.error) {
        return (
          <Message negative icon>
            <Icon name='times circle' />
            <Message.Content>
              <Message.Header>Operation Failed</Message.Header>
              {result.error.message || 'An error occurred'}
            </Message.Content>
          </Message>
        );
      }
      return null;
    }

    const hasSkipped = result.skipped_employees && result.skipped_employees.length > 0;
    const hasNotFound = result.not_found_employee_ids && result.not_found_employee_ids.length > 0;
    
    return (
      <Message 
        success={!hasSkipped && !hasNotFound} 
        warning={hasSkipped || hasNotFound}
        icon
      >
        <Icon name={hasSkipped || hasNotFound ? 'warning sign' : 'check circle'} />
        <Message.Content>
          <Message.Header>
            {isAdd ? 'Bulk Add' : 'Bulk Remove'} - {result.group_name}
          </Message.Header>
          <Message.List>
            <Message.Item>
              <strong>Total Members:</strong> {result.total_members}
            </Message.Item>
            {isAdd && (
              <>
                <Message.Item>
                  <Label color='green' size='small'>Added: {result.added_count}</Label>
                </Message.Item>
                {result.skipped_count > 0 && (
                  <Message.Item>
                    <Label color='orange' size='small'>Skipped: {result.skipped_count}</Label>
                  </Message.Item>
                )}
              </>
            )}
            {!isAdd && (
              <>
                <Message.Item>
                  <Label color='red' size='small'>Removed: {result.removed_count}</Label>
                </Message.Item>
                {result.not_found_count > 0 && (
                  <Message.Item>
                    <Label color='grey' size='small'>Not Found: {result.not_found_count}</Label>
                  </Message.Item>
                )}
              </>
            )}
          </Message.List>

          {hasSkipped && (
            <Segment basic>
              <Header as='h5' color='orange'>Skipped Employees:</Header>
              <List bulleted>
                {result.skipped_employees.map((skip, idx) => (
                  <List.Item key={idx}>
                    <strong>ID {skip.employee_id}:</strong> {skip.reason}
                  </List.Item>
                ))}
              </List>
            </Segment>
          )}

          {hasNotFound && (
            <Segment basic>
              <Header as='h5' color='grey'>Not Found Employee IDs:</Header>
              <Label.Group>
                {result.not_found_employee_ids.map((empId) => (
                  <Label key={empId}>{empId}</Label>
                ))}
              </Label.Group>
            </Segment>
          )}
        </Message.Content>
      </Message>
    );
  };


  if (loading) return <Loader active>Loading employees...</Loader>;
  if (error) return <Message negative>{error}</Message>;

  return (
    <PageContainer>
      <Header as="h2" icon>
        <Icon name='users' />
        Organization Group Members
        <Header.Subheader>
          Group ID: {id} | Total Members: {orgMembers?.count || 0}
        </Header.Subheader>
      </Header>

      <Divider />

      {/* Quick Add Button */}
      <Segment basic textAlign="center">
        <Button 
          primary 
          size="large"
          icon 
          labelPosition='left'
          onClick={() => navigate(`/app/groups/organization/${id}/add-members`)}
        >
          <Icon name='user plus' />
          Add Members from Employee List
        </Button>
      </Segment>

      {/* Bulk Operations Section */}
      <Segment raised>
        <Header as='h3'>
          <Icon name='settings' />
          Bulk Operations
        </Header>
        <Grid columns={2} stackable>
          <Grid.Column>
            <BulkCard>
              <Button 
                primary 
                fluid 
                icon 
                labelPosition='left'
                onClick={() => setShowBulkAdd(!showBulkAdd)}
              >
                <Icon name='user plus' />
                {showBulkAdd ? 'Hide' : 'Show'} Bulk Add Members
              </Button>
              
              {showBulkAdd && (
                <Form style={{ marginTop: '15px' }}>
                  <Form.Field>
                    <label>Employee IDs (comma-separated)</label>
                    <TextArea
                      placeholder='e.g., 1, 2, 3, 4, 5'
                      value={employeeIdsToAdd}
                      onChange={(e) => setEmployeeIdsToAdd(e.target.value)}
                      rows={3}
                    />
                  </Form.Field>
                  <Button 
                    positive 
                    icon 
                    labelPosition='left'
                    onClick={handleBulkAdd}
                    disabled={!employeeIdsToAdd.trim()}
                  >
                    <Icon name='plus' />
                    Add Members
                  </Button>
                </Form>
              )}
              
              {bulkAddResult && renderBulkResult(bulkAddResult, true)}
            </BulkCard>
          </Grid.Column>

          <Grid.Column>
            <BulkCard>
              <Button 
                color='red' 
                fluid 
                icon 
                labelPosition='left'
                onClick={() => setShowBulkRemove(!showBulkRemove)}
              >
                <Icon name='user delete' />
                {showBulkRemove ? 'Hide' : 'Show'} Bulk Remove Members
              </Button>
              
              {showBulkRemove && (
                <Form style={{ marginTop: '15px' }}>
                  <Form.Field>
                    <label>Employee IDs (comma-separated)</label>
                    <TextArea
                      placeholder='e.g., 1, 2, 3'
                      value={employeeIdsToRemove}
                      onChange={(e) => setEmployeeIdsToRemove(e.target.value)}
                      rows={3}
                    />
                  </Form.Field>
                  <Button 
                    negative 
                    icon 
                    labelPosition='left'
                    onClick={handleBulkRemove}
                    disabled={!employeeIdsToRemove.trim()}
                  >
                    <Icon name='minus' />
                    Remove Members
                  </Button>
                </Form>
              )}
              
              {bulkRemoveResult && renderBulkResult(bulkRemoveResult, false)}
            </BulkCard>
          </Grid.Column>
        </Grid>
      </Segment>

      <Divider />

      {/* Search and Member List */}
      <Segment>
        <Header as='h3'>
          <Icon name='search' />
          Members List
        </Header>
        <Input
          icon='search'
          iconPosition='left'
          placeholder='Search employees by name or email...'
          value={search}
          onChange={handleSearchChange}
          fluid
          style={{ marginBottom: 20 }}
        />

        {totalPages > 1 && (
          <Pagination 
            activePage={page} 
            totalPages={totalPages} 
            onPageChange={handlePageChange}
            firstItem={null}
            lastItem={null}
            siblingRange={1}
          />
        )}

        <List divided relaxed size='large'>
          {orgMembers && orgMembers.results && orgMembers.results.length > 0 ? (
            orgMembers.results.map(emp => (
              <StyledListItem key={emp.id}>
                <List.Icon name='user circle' size='big' verticalAlign='middle' color='blue' />
                <List.Content>
                  <List.Header as='a'>
                    {emp?.first_name || ''} {emp?.last_name || ''}
                    <Label size='mini' style={{ marginLeft: '10px' }}>ID: {emp.id}</Label>
                  </List.Header>
                  <List.Description>
                    <Icon name='mail' />
                    {emp?.email}
                  </List.Description>
                </List.Content>
              </StyledListItem>
            ))
          ) : (
            <Message info>
              <Message.Header>No employees found</Message.Header>
              <p>Try adjusting your search or add members using the bulk operations above.</p>
            </Message>
          )}
        </List>

        {totalPages > 1 && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <Pagination 
              activePage={page} 
              totalPages={totalPages} 
              onPageChange={handlePageChange}
              firstItem={null}
              lastItem={null}
              siblingRange={1}
            />
          </div>
        )}
      </Segment>
    </PageContainer>
  );
};

export default ListEmployees;

// Styled Components
const PageContainer = styled.div`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
`;

const BulkCard = styled.div`
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  min-height: 120px;
`;

const StyledListItem = styled(List.Item)`
  padding: 15px !important;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f0f8ff !important;
  }
`;
